import React from 'react';

/* ━━━ 1. Circular Cycle ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface CircularCycleProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
  activeIndex?: number;
}
export const CircularCycle: React.FC<CircularCycleProps> = ({
  highlightTarget, labels = ['Step 1','Step 2','Step 3','Step 4'],
  accentColor = '#06b6d4', opacity = 1, activeIndex = -1,
}) => {
  const cx = 400, cy = 250, r = 140, n = labels.length;
  const pts = labels.map((l, i) => {
    const a = (2 * Math.PI * i) / n - Math.PI / 2;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a), label: l };
  });
  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="cc-sph" cx="35%" cy="30%"><stop offset="0%" stopColor="#e2e8f0"/><stop offset="100%" stopColor={accentColor}/></radialGradient>
        <marker id="cc-arr" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto"><path d="M0,0 L8,4 L0,8Z" fill={accentColor}/></marker>
        <filter id="cc-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter>
      </defs>
      <circle cx={cx} cy={cy} r={r+40} fill="none" stroke={accentColor} strokeWidth="1" opacity="0.12"/>
      {pts.map((p, i) => {
        const nx = pts[(i+1)%n];
        const dx = nx.x-p.x, dy = nx.y-p.y, len = Math.sqrt(dx*dx+dy*dy);
        return <line key={`e${i}`} x1={p.x+dx/len*34} y1={p.y+dy/len*34} x2={nx.x-dx/len*34} y2={nx.y-dy/len*34} stroke={accentColor} strokeWidth="2.5" opacity="0.5" markerEnd="url(#cc-arr)"/>;
      })}
      {pts.map((p, i) => {
        const id = `step_${i+1}`;
        const hi = highlightTarget === id || activeIndex === i;
        return <g key={i} filter="url(#cc-sh)">
          <circle cx={p.x+3} cy={p.y+4} r="28" fill="rgba(0,0,0,0.3)"/>
          <circle cx={p.x} cy={p.y} r="28" fill="url(#cc-sph)" stroke={hi?'#fff':accentColor} strokeWidth={hi?3:1.5}/>
          <circle cx={p.x-8} cy={p.y-10} r="5" fill="rgba(255,255,255,0.45)"/>
          <text x={p.x} y={p.y+46} textAnchor="middle" dominantBaseline="middle" fill="#e2e8f0" fontSize="11" fontFamily="'Inter', system-ui, sans-serif" fontWeight="500">{p.label}</text>
          {hi && <circle cx={p.x} cy={p.y} r="36" fill="none" stroke={accentColor} strokeWidth="2" opacity="0.5"><animate attributeName="r" from="36" to="46" dur="1.2s" repeatCount="indefinite"/><animate attributeName="opacity" from="0.5" to="0" dur="1.2s" repeatCount="indefinite"/></circle>}
        </g>;
      })}
    </g>
  );
};

/* ━━━ 2. Funnel Diagram ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface FunnelDiagramProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
}
export const FunnelDiagram: React.FC<FunnelDiagramProps> = ({
  highlightTarget, labels = ['Wide','Medium','Narrow'], accentColor = '#f59e0b', opacity = 1,
}) => {
  const n = labels.length, topY = 60, h = 340 / n, baseW = 460;
  return (
    <g opacity={opacity}>
      <defs><filter id="fn-sh"><feDropShadow dx="3" dy="4" stdDeviation="4" floodOpacity="0.35"/></filter></defs>
      {labels.map((label, i) => {
        const tw = baseW * (1 - i * 0.22), bw = baseW * (1 - (i+1) * 0.22);
        const y = topY + i * h;
        const lx = 400-tw/2, rx = 400+tw/2, blx = 400-bw/2, brx = 400+bw/2;
        const id = `stage_${i+1}`;
        const hi = highlightTarget === id;
        const shade = 0.55 + i * 0.14;
        return <g key={i} filter="url(#fn-sh)">
          <path d={`M${rx},${y} L${brx},${y+h} L${brx+8},${y+h+5} L${rx+8},${y+5}Z`} fill="rgba(0,0,0,0.25)"/>
          <path d={`M${lx},${y} L${rx},${y} L${brx},${y+h} L${blx},${y+h}Z`} fill={accentColor} opacity={shade} stroke={hi?'#fff':'rgba(255,255,255,0.15)'} strokeWidth={hi?2.5:1}/>
          <line x1={lx+10} y1={y+3} x2={rx-10} y2={y+3} stroke="rgba(255,255,255,0.3)" strokeWidth="1.5"/>
          <text x={400} y={y+h/2+5} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="12" fontFamily="'Inter', system-ui, sans-serif" fontWeight="600">{label}</text>
          {hi && <path d={`M${lx-4},${y-2} L${rx+4},${y-2} L${brx+4},${y+h+2} L${blx-4},${y+h+2}Z`} fill="none" stroke="#fff" strokeWidth="2" opacity="0.6"><animate attributeName="opacity" from="0.6" to="0.1" dur="1s" repeatCount="indefinite"/></path>}
        </g>;
      })}
    </g>
  );
};

/* ━━━ 3. Linear Pipeline ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface LinearPipelineProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
  flowProgress?: number;
}
export const LinearPipeline: React.FC<LinearPipelineProps> = ({
  highlightTarget, labels = ['Input','Process','Output'], accentColor = '#10b981', opacity = 1, flowProgress = 0,
}) => {
  const n = labels.length, segW = 600/(n+0.5), startX = 100, cy = 250, pipeH = 50;
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="lp-pipe" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#475569"/><stop offset="40%" stopColor="#334155"/><stop offset="100%" stopColor="#1e293b"/></linearGradient>
        <linearGradient id="lp-flow" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={accentColor} stopOpacity="0.8"/><stop offset="100%" stopColor={accentColor} stopOpacity="0.1"/></linearGradient>
        <filter id="lp-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter>
      </defs>
      {/* Full pipe backbone */}
      <rect x={startX} y={cy-pipeH/2+4} width={segW*n} height={pipeH} rx="25" fill="rgba(0,0,0,0.3)"/>
      <rect x={startX} y={cy-pipeH/2} width={segW*n} height={pipeH} rx="25" fill="url(#lp-pipe)" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
      <line x1={startX+10} y1={cy-pipeH/2+4} x2={startX+segW*n-10} y2={cy-pipeH/2+4} stroke="rgba(255,255,255,0.15)" strokeWidth="1.5"/>
      {/* Flow indicator */}
      {flowProgress > 0 && <rect x={startX+5} y={cy-pipeH/2+6} width={(segW*n-10)*flowProgress} height={pipeH-12} rx="20" fill="url(#lp-flow)"/>}
      {/* Stage markers */}
      {labels.map((label, i) => {
        const sx = startX + i * segW + segW/2;
        const id = `stage_${i+1}`;
        const hi = highlightTarget === id;
        return <g key={i} filter="url(#lp-sh)">
          <circle cx={sx+2} cy={cy+3} r="22" fill="rgba(0,0,0,0.3)"/>
          <circle cx={sx} cy={cy} r="22" fill={accentColor} opacity={hi?1:0.7} stroke={hi?'#fff':accentColor} strokeWidth={hi?3:1.5}/>
          <circle cx={sx-6} cy={cy-7} r="4" fill="rgba(255,255,255,0.4)"/>
          <text x={sx} y={cy+42} textAnchor="middle" dominantBaseline="middle" fill="#e2e8f0" fontSize="11" fontFamily="'Inter', system-ui, sans-serif" fontWeight="500">{label}</text>
          {hi && <circle cx={sx} cy={cy} r="30" fill="none" stroke="#fff" strokeWidth="2" opacity="0.5"><animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite"/></circle>}
          {i < n-1 && <text x={sx + segW/2} y={cy+5} textAnchor="middle" fill={accentColor} fontSize="16" fontFamily="'Inter', system-ui, sans-serif">▸</text>}
        </g>;
      })}
    </g>
  );
};

/* ━━━ 4. Waterfall Steps ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface WaterfallStepsProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
}
export const WaterfallSteps: React.FC<WaterfallStepsProps> = ({
  highlightTarget, labels = ['Phase 1','Phase 2','Phase 3','Phase 4'], accentColor = '#8b5cf6', opacity = 1,
}) => {
  const n = labels.length, stepW = 520/n, stepH = 60, startX = 140, startY = 80;
  return (
    <g opacity={opacity}>
      <defs><filter id="wf-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter></defs>
      {labels.map((label, i) => {
        const x = startX + i * (stepW * 0.85), y = startY + i * (stepH + 15);
        const id = `step_${i+1}`;
        const hi = highlightTarget === id;
        return <g key={i} filter="url(#wf-sh)">
          {/* 3D top face */}
          <path d={`M${x},${y} L${x+stepW},${y} L${x+stepW+10},${y-8} L${x+10},${y-8}Z`} fill={accentColor} opacity="0.4"/>
          {/* 3D right face */}
          <path d={`M${x+stepW},${y} L${x+stepW},${y+stepH} L${x+stepW+10},${y+stepH-8} L${x+stepW+10},${y-8}Z`} fill="rgba(0,0,0,0.3)"/>
          {/* Front face */}
          <rect x={x} y={y} width={stepW} height={stepH} rx="4" fill={accentColor} opacity={hi?1:0.65} stroke={hi?'#fff':'rgba(255,255,255,0.12)'} strokeWidth={hi?2:1}/>
          <line x1={x+4} y1={y+3} x2={x+stepW-4} y2={y+3} stroke="rgba(255,255,255,0.25)" strokeWidth="1"/>
          <text x={x+stepW/2} y={y+stepH/2+5} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="12" fontFamily="'Inter', system-ui, sans-serif" fontWeight="600">{label}</text>
          {/* Waterfall connector */}
          {i < n-1 && <path d={`M${x+stepW*0.7},${y+stepH} L${x+stepW*0.7},${y+stepH+15} L${x+stepW*0.85+stepW*0.15},${y+stepH+15}`} fill="none" stroke={accentColor} strokeWidth="2" opacity="0.5" strokeDasharray="4,3"/>}
        </g>;
      })}
    </g>
  );
};

/* ━━━ 5. Spiral Path ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface SpiralPathProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
  activeIndex?: number;
}
export const SpiralPath: React.FC<SpiralPathProps> = ({
  highlightTarget, labels = ['Start','Growth','Maturity','Peak'], accentColor = '#ec4899', opacity = 1, activeIndex = -1,
}) => {
  const cx = 400, cy = 260, n = labels.length;
  const spiralPts = labels.map((_, i) => {
    const a = (Math.PI * 1.8 * i) / (n - 1);
    const r = 40 + i * 40;
    return { x: cx + r * Math.cos(a - Math.PI), y: cy + r * Math.sin(a - Math.PI) };
  });
  const pathD = spiralPts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  return (
    <g opacity={opacity}>
      <defs>
        <radialGradient id="sp-nd" cx="35%" cy="30%"><stop offset="0%" stopColor="#e2e8f0"/><stop offset="100%" stopColor={accentColor}/></radialGradient>
        <filter id="sp-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter>
      </defs>
      {/* Spiral path shadow */}
      <path d={pathD} fill="none" stroke="rgba(0,0,0,0.3)" strokeWidth="4" transform="translate(2,3)"/>
      <path d={pathD} fill="none" stroke={accentColor} strokeWidth="3" opacity="0.4" strokeLinecap="round"/>
      <path d={pathD} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeLinecap="round"/>
      {spiralPts.map((p, i) => {
        const id = `step_${i+1}`;
        const hi = highlightTarget === id || activeIndex === i;
        return <g key={i} filter="url(#sp-sh)">
          <circle cx={p.x+2} cy={p.y+3} r="20" fill="rgba(0,0,0,0.3)"/>
          <circle cx={p.x} cy={p.y} r="20" fill="url(#sp-nd)" stroke={hi?'#fff':accentColor} strokeWidth={hi?3:1.5}/>
          <circle cx={p.x-5} cy={p.y-7} r="4" fill="rgba(255,255,255,0.4)"/>
          <text x={p.x} y={p.y+34} textAnchor="middle" dominantBaseline="middle" fill="#e2e8f0" fontSize="11" fontFamily="'Inter', system-ui, sans-serif">{labels[i]}</text>
          {hi && <circle cx={p.x} cy={p.y} r="28" fill="none" stroke="#fff" strokeWidth="2" opacity="0.5"><animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite"/></circle>}
        </g>;
      })}
    </g>
  );
};

/* ━━━ 6. Parallel Process ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface ParallelProcessProps {
  highlightTarget?: string;
  labels?: string[];
  accentColor?: string;
  opacity?: number;
  flowProgress?: number;
}
export const ParallelProcess: React.FC<ParallelProcessProps> = ({
  highlightTarget, labels = ['Thread A','Thread B','Thread C'], accentColor = '#f97316', opacity = 1, flowProgress = 0,
}) => {
  const n = labels.length, laneH = 280/n, startX = 120, endX = 680, topY = 110;
  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="pp-lane" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1e293b"/><stop offset="100%" stopColor="#0f172a"/></linearGradient>
        <filter id="pp-sh"><feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.35"/></filter>
      </defs>
      {/* Fork bar */}
      <rect x={startX-10} y={topY-10} width="14" height={laneH*n+20} rx="7" fill={accentColor} opacity="0.7" filter="url(#pp-sh)"/>
      {/* Join bar */}
      <rect x={endX-4} y={topY-10} width="14" height={laneH*n+20} rx="7" fill={accentColor} opacity="0.7" filter="url(#pp-sh)"/>
      {labels.map((label, i) => {
        const y = topY + i * laneH + laneH / 2;
        const id = `lane_${i+1}`;
        const hi = highlightTarget === id;
        return <g key={i}>
          {/* Lane background */}
          <rect x={startX+10} y={y-16} width={endX-startX-20} height="32" rx="16" fill="url(#pp-lane)" stroke="rgba(255,255,255,0.06)" strokeWidth="1"/>
          {/* Flow progress */}
          {flowProgress > 0 && <rect x={startX+14} y={y-12} width={(endX-startX-28)*flowProgress} height="24" rx="12" fill={accentColor} opacity="0.25"/>}
          {/* Lane arrow */}
          <line x1={startX+20} y1={y} x2={endX-20} y2={y} stroke={accentColor} strokeWidth="1.5" opacity="0.3" strokeDasharray="6,4"/>
          {/* Node */}
          <circle cx={startX + (endX-startX)*0.5} cy={y} r="16" fill={accentColor} opacity={hi?1:0.6} stroke={hi?'#fff':'none'} strokeWidth="2" filter="url(#pp-sh)"/>
          <circle cx={startX + (endX-startX)*0.5 - 4} cy={y-5} r="3" fill="rgba(255,255,255,0.35)"/>
          <text x={startX + (endX-startX)*0.5} y={y-24} textAnchor="middle" dominantBaseline="middle" fill="#e2e8f0" fontSize="11" fontFamily="'Inter', system-ui, sans-serif" fontWeight="500">{label}</text>
          {hi && <circle cx={startX+(endX-startX)*0.5} cy={y} r="24" fill="none" stroke="#fff" strokeWidth="2" opacity="0.5"><animate attributeName="opacity" from="0.5" to="0" dur="1s" repeatCount="indefinite"/></circle>}
        </g>;
      })}
    </g>
  );
};
